/**
 * Australian Bank Home Loan Rates
 *
 * Data based on publicly available rates as of January 2026.
 * Rates are indicative and subject to change.
 *
 * Sources:
 * - Individual bank websites
 * - Canstar, Mozo, Money.com.au comparisons
 */

export interface LenderRate {
  lender: string;
  logo: string;
  variableRate: number;
  comparisonRate: number;
  fixedRates: {
    oneYear: number;
    twoYear: number;
    threeYear: number;
    fiveYear: number;
  };
  features: string[];
  loanType: "owner" | "investor" | "both";
  minLoanAmount: number;
  maxLVR: number;
}

export const majorBankRates: LenderRate[] = [
  {
    lender: "Commonwealth Bank",
    logo: "/lenders/commbank.png",
    variableRate: 6.19,
    comparisonRate: 6.21,
    fixedRates: {
      oneYear: 5.49,
      twoYear: 5.34,
      threeYear: 5.34,
      fiveYear: 5.94,
    },
    features: ["Offset account", "Redraw facility", "Extra repayments"],
    loanType: "both",
    minLoanAmount: 10000,
    maxLVR: 95,
  },
  {
    lender: "Westpac",
    logo: "/lenders/westpac.png",
    variableRate: 6.14,
    comparisonRate: 6.16,
    fixedRates: {
      oneYear: 5.19,
      twoYear: 5.29,
      threeYear: 5.39,
      fiveYear: 5.59,
    },
    features: ["Offset account", "Redraw facility", "Package discounts"],
    loanType: "both",
    minLoanAmount: 20000,
    maxLVR: 95,
  },
  {
    lender: "NAB",
    logo: "/lenders/nab.png",
    variableRate: 6.24,
    comparisonRate: 6.26,
    fixedRates: {
      oneYear: 5.29,
      twoYear: 5.39,
      threeYear: 5.49,
      fiveYear: 5.69,
    },
    features: ["Offset account", "Redraw facility", "Rate lock option"],
    loanType: "both",
    minLoanAmount: 20000,
    maxLVR: 95,
  },
  {
    lender: "Macquarie",
    logo: "/lenders/macquarie.png",
    variableRate: 5.95,
    comparisonRate: 5.97,
    fixedRates: {
      oneYear: 5.29,
      twoYear: 5.39,
      threeYear: 5.49,
      fiveYear: 5.69,
    },
    features: ["Offset account", "100% offset", "No ongoing fees"],
    loanType: "both",
    minLoanAmount: 150000,
    maxLVR: 90,
  },
  {
    lender: "ING",
    logo: "/lenders/ing.png",
    variableRate: 5.89,
    comparisonRate: 5.91,
    fixedRates: {
      oneYear: 5.39,
      twoYear: 5.49,
      threeYear: 5.59,
      fiveYear: 5.79,
    },
    features: ["Offset account", "No monthly fees", "Cashback offers"],
    loanType: "owner",
    minLoanAmount: 150000,
    maxLVR: 80,
  },
  {
    lender: "Bankwest",
    logo: "/lenders/bankwest.png",
    variableRate: 5.94,
    comparisonRate: 5.96,
    fixedRates: {
      oneYear: 5.29,
      twoYear: 5.39,
      threeYear: 5.49,
      fiveYear: 5.69,
    },
    features: ["Offset account", "Redraw facility", "Online only discount"],
    loanType: "both",
    minLoanAmount: 20000,
    maxLVR: 90,
  },
  {
    lender: "St.George",
    logo: "/lenders/st-george.webp",
    variableRate: 6.09,
    comparisonRate: 6.11,
    fixedRates: {
      oneYear: 5.29,
      twoYear: 5.39,
      threeYear: 5.49,
      fiveYear: 5.69,
    },
    features: ["Offset account", "Redraw facility", "Package pricing"],
    loanType: "both",
    minLoanAmount: 20000,
    maxLVR: 95,
  },
  {
    lender: "Suncorp",
    logo: "/lenders/suncorp.png",
    variableRate: 5.99,
    comparisonRate: 6.01,
    fixedRates: {
      oneYear: 5.34,
      twoYear: 5.44,
      threeYear: 5.54,
      fiveYear: 5.74,
    },
    features: ["Offset account", "Redraw facility", "Flexible terms"],
    loanType: "both",
    minLoanAmount: 20000,
    maxLVR: 90,
  },
];

/**
 * Get lowest variable rate
 */
export function getLowestVariableRate(): number {
  return Math.min(...majorBankRates.map((r) => r.variableRate));
}

/**
 * Get lowest fixed rate (any term)
 */
export function getLowestFixedRate(): number {
  const allFixed = majorBankRates.flatMap((r) => [
    r.fixedRates.oneYear,
    r.fixedRates.twoYear,
    r.fixedRates.threeYear,
    r.fixedRates.fiveYear,
  ]);
  return Math.min(...allFixed);
}

/**
 * RBA Cash Rate History
 */
export const rbaCashRateHistory = [
  { date: "Feb 2025", rate: 4.10 },
  { date: "May 2025", rate: 3.85 },
  { date: "Aug 2025", rate: 3.60 },
  { date: "Nov 2025", rate: 3.60 },
  { date: "Jan 2026", rate: 3.60 },
];

/**
 * Last updated timestamp
 */
export const ratesLastUpdated = "2 January 2026";
